"use client"

import React, { useEffect, useState, useCallback } from 'react';
import { Search, Briefcase, Plus, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@t-one-internal/design-system/lib/components/ui/popover';
import { Spinner } from '@t-one-internal/design-system';
import apiClient from '../../api/apiClient';

export interface JobTitleItem {
  job_title_id: string;
  name: string;
  unit?: string;
  total_employees?: number;
}

interface SmartJobTitleDropdownProps {
  scopeInstanceId?: string;
  selectedJobTitleId?: string | null;
  selectedJobTitleName?: string | null;
  onSelectJobTitle: (jobTitle: JobTitleItem | null) => void;
  className?: string;
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const SmartJobTitleDropdown: React.FC<SmartJobTitleDropdownProps> = ({
  scopeInstanceId,
  selectedJobTitleId,
  selectedJobTitleName,
  onSelectJobTitle,
  className = '',
  allowClear = false,
  placeholder = 'Select a job title',
  disabled = false,
  error,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [jobTitles, setJobTitles] = useState<JobTitleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchJobTitles = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      const scopeId = scopeInstanceId?.trim();
      if (scopeId) params.scope_instance_id = scopeId;
      const { data } = await apiClient.get<{ data: JobTitleItem[]; message?: string; count?: number }>('/job-title/', { params });
      setJobTitles(data?.data || []);
    } catch {
      setJobTitles([]);
    } finally {
      setLoading(false);
    }
  }, [scopeInstanceId]);

  useEffect(() => {
    if (isOpen) {
      fetchJobTitles();
    }
  }, [isOpen, fetchJobTitles]);

  const handleSelect = (jobTitle: JobTitleItem | null) => {
    onSelectJobTitle(jobTitle);
    setIsOpen(false);
    setSearchQuery('');
    setCreateError(null);
  };

  const handleClear = () => {
    onSelectJobTitle(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  const query = searchQuery.trim().toLowerCase();
  const filtered = !query
    ? jobTitles
    : jobTitles.filter((jt) => jt.name.toLowerCase().includes(query));

  const hasExactMatch = query && jobTitles.some((jt) => jt.name.toLowerCase() === query);
  const showAddOption = !!scopeInstanceId?.trim() && !!query && !hasExactMatch && query.length > 0;

  const handleCreateAndSelect = async () => {
    if (!scopeInstanceId?.trim() || !query) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const { data } = await apiClient.post<{ data: JobTitleItem; message?: string }>('/job-title/', {
        name: searchQuery.trim(),
        scope_instance_id: scopeInstanceId.trim(),
      });
      const created = data?.data;
      if (created) {
        setJobTitles((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        handleSelect(created);
      }
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create job title');
    } finally {
      setCreateLoading(false);
    }
  };

  const selectedJobTitle = jobTitles.find(
    (jt) => String(jt.job_title_id) === selectedJobTitleId
  );
  const displayName = selectedJobTitle?.name ?? selectedJobTitleName ?? '';

  return (
    <div className={className}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`w-full h-9 px-3 text-left bg-white border rounded-md transition-colors flex items-center justify-between ${
              error
                ? 'border-red-300 focus:ring-red-400'
                : 'border-slate-200 hover:border-slate-300 focus:ring-slate-400'
            } focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {displayName ? (
              <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                <div className="w-5 h-5 rounded bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-3 h-3 text-indigo-600" />
                </div>
                <span className="text-sm text-slate-700 truncate min-w-0">{displayName}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                <span className="text-sm text-slate-500 truncate min-w-0">{placeholder}</span>
              </div>
            )}
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="flex flex-col max-h-72 overflow-hidden bg-white rounded-lg shadow-lg border border-slate-200">
            {/* Search */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search job titles..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-slate-400"
                  autoFocus
                />
              </div>
            </div>

            {/* Clear option */}
            {allowClear && !searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 border-b border-slate-100"
              >
                <X className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">Clear selection</span>
              </button>
            )}

            {/* Add new job title option */}
            {showAddOption && (
              <div className="border-b border-slate-100 bg-indigo-50/50">
                <button
                  type="button"
                  disabled={createLoading}
                  onClick={handleCreateAndSelect}
                  className="w-full px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors flex items-center gap-2 disabled:opacity-60"
                >
                  <Plus className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0 text-left">
                    <span className="text-sm font-medium text-slate-800">
                      Add &quot;{searchQuery.trim()}&quot; as a job title
                    </span>
                    {createError && (
                      <p className="text-xs text-red-600 mt-0.5">{createError}</p>
                    )}
                  </div>
                </button>
              </div>
            )}

            {/* Job title list */}
            <div className="overflow-y-auto flex-1 min-h-0">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner size="sm" />
                </div>
              ) : filtered.length > 0 ? (
                <div className="py-1">
                  {filtered.map((jt) => (
                    <button
                      key={jt.job_title_id}
                      type="button"
                      onClick={() => handleSelect(jt)}
                      className={`w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                        selectedJobTitleId === String(jt.job_title_id) ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-sm truncate block ${
                            selectedJobTitleId === String(jt.job_title_id)
                              ? 'text-indigo-900 font-medium'
                              : 'text-slate-700'
                          }`}
                        >
                          {jt.name}
                        </span>
                        {jt.unit && (
                          <span className="text-xs text-slate-400 truncate block">
                            {jt.unit}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : !showAddOption ? (
                <div className="px-3 py-6 text-center text-slate-500 text-sm">
                  {searchQuery ? 'No matching job titles found.' : 'No job titles available'}
                </div>
              ) : null}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};
